import { hasOwnProperty } from "@glideapps/ts-necessities";

import {
    asArrayValue,
    asDate,
    asMaybeString,
    asString,
    AsyncRecomputingValueProducer,
    isLoadedDefined,
    makeObjectInspector,
    makeWebhookValue,
    SubscriptionClient,
    SubscriptionManager,
} from "./component-helpers";
import { LOADING, DeserializationSpec, DeserializableKind, PrimitiveValue } from "./computation-types";
import { makeValueFromJSON, setDataDeep, Data as GenericData } from "./data";
import { registerDeserializable } from "./deserialization";
import { loadPublishedAppData } from "../Database";
import { sleep } from "../Support";
import {
    AppFacilities,
    AppEnvironment,
    AppOther,
    ValueProducer,
    PotentialValue,
    Evaluable,
    EvalEnvironment,
    Context,
    Data,
    Value,
    ForceValueProducer,
    ObjectInspector,
    Loading,
} from "./types";
import { BuiltinCallable } from "./primitives";
import { registerBuiltinFunction } from "./functions";
import { CallAPIColumnBody, WebhookValues } from "../firebase-function-types";

class HTTPFetchValueProducer extends AsyncRecomputingValueProducer<AppOther, AppEnvironment> {
    constructor(private readonly _appFacilities: AppFacilities, private readonly _url: ValueProducer) {
        super();
    }

    protected firstSubscribed(): void {
        super.firstSubscribed();
        this._url.subscribe(this);
    }

    protected lastUnsubscribed(): void {
        super.lastUnsubscribed();
        this._url.unsubscribe(this);
    }

    protected async recomputeAsync(): Promise<PotentialValue> {
        const urlValue = this._url.current;
        if (urlValue === LOADING) return undefined;

        const url = asString(urlValue);
        const json = await this._appFacilities.fetchJSON(url);
        if (json === undefined) return undefined;
        return makeValueFromJSON<AppOther, AppEnvironment>(json, false);
    }

    public getObjectInspector(force: ForceValueProducer | undefined): ObjectInspector | Loading | undefined {
        return makeObjectInspector(this, force);
    }
}

interface WebhookIDAndValues {
    readonly webhookID: string;
    readonly params: WebhookValues;
}

class UserAPIFetchValueProducer
    extends AsyncRecomputingValueProducer<AppOther, AppEnvironment>
    implements SubscriptionClient<AppOther, AppEnvironment, WebhookIDAndValues | Loading | undefined> {
    private readonly _subscriptionManager = new SubscriptionManager(this);

    constructor(
        private readonly _appEnvironment: AppEnvironment,
        private readonly _webhookID: ValueProducer,
        private readonly _params: ValueProducer
    ) {
        super();
    }

    public recompute(force: ForceValueProducer): WebhookIDAndValues | Loading | undefined {
        const webhookIDValue = force(this._webhookID);
        const paramsValue = force(this._params);

        if (webhookIDValue === LOADING || paramsValue === LOADING) return LOADING;

        const webhookID = asMaybeString(webhookIDValue);
        if (webhookID === undefined || paramsValue === undefined) return undefined;

        if (!isLoadedDefined(paramsValue)) return paramsValue;

        const paramsArray = asArrayValue(paramsValue);

        const params: WebhookValues = {};

        for (let i = 0; i < paramsArray.length; i += 2) {
            const maybeName = force(paramsArray[i + 0]);
            if (maybeName === LOADING) return LOADING;
            const maybeValue = force(paramsArray[i + 1]);
            if (maybeValue === LOADING) return LOADING;

            const name = asMaybeString(maybeName);
            if (name === undefined) continue;

            const value = makeWebhookValue(maybeValue);
            if (value === undefined) continue;

            params[name] = value;
        }

        return { webhookID, params };
    }

    protected lastUnsubscribed(): void {
        super.lastUnsubscribed();
        this._subscriptionManager.unsubscribe();
    }

    protected async recomputeAsync(): Promise<PotentialValue> {
        const idAndParams = this._subscriptionManager.run(this.hasSubscribers);
        if (!isLoadedDefined(idAndParams)) return idAndParams;

        const body: CallAPIColumnBody = {
            appID: this._appEnvironment.appID,
            ...idAndParams,
        };
        const response = await this._appEnvironment.appFacilities.callAuthIfAvailableCloudFunction(
            "callAPIColumn",
            body,
            {}
        );
        if (response?.ok !== true) return undefined;

        const json = await response.json();

        if (json === undefined) return undefined;
        if (!hasOwnProperty(json, "type") || typeof json.type !== "string") return undefined;
        if (!hasOwnProperty(json, "value")) return undefined;

        let value: PrimitiveValue;

        if (json.type === "string") {
            if (typeof json.value !== "string") return undefined;
            value = json.value;
        } else if (json.type === "number") {
            if (typeof json.value !== "number") return undefined;
            value = json.value;
        } else if (json.type === "boolean") {
            if (typeof json.value !== "boolean") return undefined;
            value = json.value;
        } else if (json.type === "date-time") {
            if (typeof json.value !== "string") return undefined;
            value = asDate(json.value);
        } else {
            return undefined;
        }

        return value;
    }

    public getObjectInspector(force: ForceValueProducer | undefined): ObjectInspector | Loading | undefined {
        return makeObjectInspector(this, force);
    }
}

class HTTPFetchEvaluable implements Evaluable {
    constructor(private readonly _url: Evaluable) {}

    public evalInEnvironment(env: EvalEnvironment, context: Context): ValueProducer {
        return new HTTPFetchValueProducer(
            env.root.appEnvironment.appFacilities,
            this._url.evalInEnvironment(env, context)
        );
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return ["HTTPFetch", "evaluable", ["url", "evaluable"]];
    }
}

// FIXME: Technically we'd have to cache by database, but this
// is just a hack anyway.
// https://github.com/quicktype/glide/issues/1
const publishedAppDataCache: Map<string, Data> = new Map();

async function fetchPublishedAppData(appEnvironment: AppEnvironment, appID: string): Promise<Value> {
    const cachedData = publishedAppDataCache.get(appID);
    if (cachedData !== undefined) return cachedData.current;

    for (;;) {
        const appData = await loadPublishedAppData(appEnvironment.database, appID);
        if (appData === undefined) {
            await sleep(100);
            continue;
        }

        const newData = GenericData.fromJSON<AppOther, AppEnvironment>(appData.data);
        const existingData = publishedAppDataCache.get(appID);
        if (existingData !== undefined) {
            setDataDeep(existingData, newData);
            return existingData.current;
        } else {
            publishedAppDataCache.set(appID, newData);
            return newData.current;
        }
    }
}

async function getPublishedAppData(appEnvironment: AppEnvironment, appID: string): Promise<Value> {
    const maybeData = publishedAppDataCache.get(appID);
    if (maybeData !== undefined) {
        return maybeData.current;
    }

    return await fetchPublishedAppData(appEnvironment, appID);
}

class PublishedAppDataFetchValueProducer extends AsyncRecomputingValueProducer<AppOther, AppEnvironment> {
    constructor(private readonly _appEnvironment: AppEnvironment, private readonly _id: ValueProducer) {
        super();
    }

    protected firstSubscribed(): void {
        super.firstSubscribed();
        this._id.subscribe(this);
    }

    protected lastUnsubscribed(): void {
        super.lastUnsubscribed();
        this._id.unsubscribe(this);
    }

    protected async recomputeAsync(): Promise<PotentialValue> {
        const idValue = this._id.current;
        if (idValue === LOADING) return LOADING;

        const id = asString(idValue);

        return await getPublishedAppData(this._appEnvironment, id);
    }

    public getObjectInspector(force: ForceValueProducer | undefined): ObjectInspector | Loading | undefined {
        return makeObjectInspector(this, force);
    }
}

class PublishedAppDataFetchEvaluable implements Evaluable {
    constructor(private readonly _id: Evaluable, _password: Evaluable | undefined) {}

    public evalInEnvironment(env: EvalEnvironment, context: Context): ValueProducer {
        return new PublishedAppDataFetchValueProducer(
            env.root.appEnvironment,
            this._id.evalInEnvironment(env, context)
        );
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return ["PublishedAppDataFetch", "evaluable", ["id", "evaluable"], ["password", "evaluable", false]];
    }
}

export function registerFetchers(): void {
    registerDeserializable(HTTPFetchEvaluable);
    registerDeserializable(PublishedAppDataFetchEvaluable);
    registerBuiltinFunction("UserAPIFetch", new BuiltinCallable(UserAPIFetchValueProducer));
}
